import { useMemo, useState, useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Search, ArrowRight, Check } from "lucide-react";
import { SERVICES, CATEGORIES } from "../data/divisions";
import { Reveal } from "../components/Reveal";

function FeaturedBlock({ d, idx }) {
  return (
    <section
      id={d.id}
      className={`${idx % 2 === 0 ? "bg-white" : "bg-stone"} scroll-mt-24`}
      data-testid={`division-section-${d.id}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-center">
        {d.image && (
          <Reveal className={`md:col-span-6 ${idx % 2 === 0 ? "" : "md:order-2"}`}>
            <div className="rounded-3xl overflow-hidden card-lift">
              <img src={d.image} alt={d.name} loading="lazy" className="w-full h-[420px] md:h-[520px] object-cover" />
            </div>
          </Reveal>
        )}
        <Reveal className={`md:col-span-6 ${idx % 2 === 0 ? "" : "md:order-1"} ${!d.image ? "md:col-span-12" : ""}`} delay={1}>
          <div className="flex items-center gap-3 text-forest mb-4">
            <d.icon size={18}/>
            <span className="text-xs uppercase tracking-[0.3em] font-semibold">{d.category}</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-extrabold text-navy tracking-tighter leading-[0.98]">{d.name}</h2>
          <p className="mt-4 text-xl text-forest font-medium" style={{fontFamily:"'Fraunces', serif", fontStyle:"italic"}}>{d.tagline}</p>
          <p className="mt-6 text-slate leading-relaxed max-w-xl">{d.description}</p>
          {d.services && (
            <ul className="mt-8 grid sm:grid-cols-2 gap-3">
              {d.services.map((s) => (
                <li key={s} className="flex items-start gap-2 text-navy">
                  <Check size={16} className="text-forest mt-1 shrink-0" />
                  <span className="text-sm">{s}</span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-10">
            <Link to={`/partner?type=service&division=${d.id}`} className="btn-primary rounded-full px-6 py-3 text-sm font-semibold inline-flex items-center gap-2" data-testid={`division-cta-${d.id}`}>
              Work with this division <ArrowRight size={16}/>
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Divisions() {
  const { hash } = useLocation();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#",""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  const featured = useMemo(() => SERVICES.filter((s) => s.featured && s.services), []);

  const directory = useMemo(() => {
    const q = query.trim().toLowerCase();
    return SERVICES.filter((s) => {
      const inCat = category === "All" || s.category === category;
      const inSearch = !q || s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q) || s.category.toLowerCase().includes(q);
      return inCat && inSearch;
    });
  }, [query, category]);

  return (
    <div className="pt-16 md:pt-20" data-testid="page-divisions">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28">
        <div className="text-xs uppercase tracking-[0.3em] text-forest font-semibold mb-6">Our Divisions & Services</div>
        <h1 className="text-5xl sm:text-6xl lg:text-8xl font-extrabold text-navy tracking-tighter leading-[0.95] max-w-5xl">
          30+ services. One accountable group.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-slate max-w-2xl leading-relaxed">
          A directory of every commercial category Stone Shire Group operates in — search, filter, and get in touch.
        </p>
      </section>

      {/* Directory */}
      <section id="directory" className="scroll-mt-24">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div className="relative w-full md:max-w-md">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate" />
              <input
                data-testid="services-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search services…"
                className="w-full rounded-full border border-stone bg-white pl-11 pr-4 py-3 text-sm text-navy placeholder-slate focus:border-forest focus:ring-2 focus:ring-forest/20 outline-none"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button
                  key={c}
                  onClick={() => setCategory(c)}
                  data-testid={`services-filter-${c.replace(/[^a-z]/gi,'-').toLowerCase()}`}
                  className={`rounded-full px-4 py-1.5 text-xs font-semibold border transition-colors ${
                    category === c ? "bg-navy text-white border-navy" : "bg-white text-navy border-stone hover:border-navy/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="text-xs text-slate mb-4" data-testid="services-count">
            Showing {directory.length} of {SERVICES.length} services
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {directory.map((s, i) => (
              <Reveal key={s.id} delay={Math.min(i, 6)}>
                <Link
                  to={`/partner?type=service&division=${s.id}`}
                  data-testid={`service-card-${s.id}`}
                  className="group block h-full rounded-2xl border border-stone bg-white p-6 card-lift"
                >
                  <div className="flex items-center gap-3 text-forest">
                    <div className="rounded-xl border border-stone p-2.5 group-hover:border-forest transition-colors">
                      <s.icon size={18} />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-semibold text-slate">{s.category}</div>
                  </div>
                  <div className="mt-4 text-lg font-bold text-navy tracking-tight leading-snug">{s.name}</div>
                  <p className="mt-2 text-sm text-slate leading-relaxed">{s.tagline}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-forest opacity-0 group-hover:opacity-100 transition-opacity">
                    Enquire <ArrowRight size={12}/>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>

          {directory.length === 0 && (
            <div className="rounded-2xl border border-stone bg-white p-10 text-center text-slate mt-4" data-testid="services-empty">
              No services match your search. <button className="text-forest font-semibold underline" onClick={() => { setQuery(""); setCategory("All"); }}>Reset filters</button>
            </div>
          )}
        </div>
      </section>

      {/* Featured deep-dive divisions */}
      <div className="mt-24 border-t border-stone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 pb-4">
          <div className="text-xs uppercase tracking-[0.3em] text-forest font-semibold mb-4">Deep dive</div>
          <h2 className="text-3xl md:text-5xl font-bold text-navy tracking-tight max-w-3xl">Flagship divisions.</h2>
        </div>
      </div>
      {featured.map((d, i) => <FeaturedBlock key={d.id} d={d} idx={i} />)}
    </div>
  );
}
